"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, MessageSquare } from "lucide-react";
import { useStore } from "@/lib/store";
import { Avatar } from "@/components/ui/Avatar";
import { Markdown } from "@/components/ui/Markdown";
import { formatRelative } from "@/lib/utils";
import { HumanMentionPicker } from "./HumanMentionPicker";
import type { Comment } from "@/lib/types";

/**
 * Polymorphic comments UI used by docs + notes (and could be plugged
 * into any other entity surface). Issue comments still render via
 * IssueDetail's bespoke composer for now — that one has more affordances
 * (BotBadge, etc.) and predates this component.
 *
 * Composer posts via store.addComment({ parentType, parentId, body }),
 * which is the polymorphic call shape. Realtime sync via the workspace's
 * `comments` postgres_changes channel keeps every open tab in lockstep.
 *
 * Mentions: typing `@<bot-handle>` triggers the agent runner via
 * dispatchCommentMentions (PAUT-30) — works identically across parent
 * types since PAUT-31.
 */
export function CommentsSection({
  parentType,
  parentId,
  comments,
  defaultOpen = true,
  canWrite = true,
}: {
  parentType: "doc" | "note";
  parentId: string;
  comments: Comment[];
  defaultOpen?: boolean;
  canWrite?: boolean;
}) {
  const workspaceId = useStore((s) => s.activeWorkspaceId);
  const users = useStore((s) => s.users);
  const userId = useStore((s) => s.currentUserId);
  const addComment = useStore((s) => s.addComment);

  const [body, setBody] = useState("");
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0), pending = useRef(false), textarea = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const reset = () => { ++generation.current; pending.current = false; setBody(""); setReplyToId(null); setError(null); setSaving(false); };
    reset();
    const unsubscribe = useStore.subscribe((state, previous) => {
      const selectedChanged = parentType === "doc" ? state.selectedDocId !== previous.selectedDocId : state.selectedNoteId !== previous.selectedNoteId;
      if (state.activeWorkspaceId !== workspaceId || state.currentUserId !== userId || selectedChanged) reset();
    });
    return () => { ++generation.current; unsubscribe(); };
  }, [workspaceId, userId, parentType, parentId]);
  const [open, setOpen] = useState(defaultOpen || comments.length === 0);

  const sorted = useMemo(
    () => [...comments].sort((a, b) => a.createdAt - b.createdAt),
    [comments]
  );

  async function submit() {
    if (!canWrite || pending.current || !body.trim()) return;
    const current = generation.current;
    pending.current = true; setSaving(true); setError(null);
    try {
      await addComment({ parentType, parentId, body: body.trim(), replyToId });
      if (generation.current === current) { setBody(""); setReplyToId(null); }
    } catch {
      if (generation.current === current) setError("Não foi possível confirmar o comentário. Seu texto foi preservado.");
    } finally {
      if (generation.current === current) { pending.current = false; setSaving(false); }
    }
  }

  return (
    <div className="border-t border-[var(--border)] mt-4 pt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="focus-ring flex min-h-11 items-center gap-2 text-[12px] font-medium text-[var(--fg-secondary)] hover:text-[var(--fg)] transition-colors w-full mb-2"
      >
        {open ? (
          <ChevronDown size={11} className="text-[var(--fg-tertiary)]" />
        ) : (
          <ChevronRight size={11} className="text-[var(--fg-tertiary)]" />
        )}
        <MessageSquare size={12} className="text-[var(--accent)]" />
        Comments
        <span className="text-[var(--fg-muted)]">{sorted.length}</span>
      </button>

      {open && (
        <div className="space-y-3">
          {sorted.length === 0 ? (
            <div className="text-[11.5px] text-[var(--fg-muted)] italic px-1">
              {canWrite ? (
                <>No comments yet. Mention <code className="text-[10.5px]">@bot-handle</code> to ping a workspace agent.</>
              ) : (
                "No comments yet."
              )}
            </div>
          ) : (
            sorted.map((c) => {
              const author = users.find((u) => u.id === c.userId);
              return (
                <div key={c.id} className="flex items-start gap-2">
                  <Avatar user={author} size={22} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 text-[11.5px]">
                      <span className="font-medium text-[var(--fg)]">
                        {author?.name ?? "Unknown"}
                      </span>
                      <span className="text-[var(--fg-muted)]">
                        {formatRelative(c.createdAt)}
                      </span>
                      {author?.isBot && (
                        <span className="text-[10px] text-[var(--green)]">bot</span>
                      )}
                    </div>
                    {c.replyToId && <p className="text-[11px] text-[var(--fg-muted)]">Resposta a {users.find((person) => person.id === comments.find((parent) => parent.id === c.replyToId)?.userId)?.name ?? "comentário anterior"}</p>}
                    <div className="mt-0.5 text-[12.5px] text-[var(--fg-secondary)]">
                      <Markdown content={c.body} />
                    </div>
                    {canWrite && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.id) && <button type="button" disabled={saving} className="focus-ring min-h-11 px-2 text-xs text-[var(--accent)]" onClick={() => { setReplyToId(c.id); setError(null); textarea.current?.focus(); }}>Responder a {author?.name ?? "comentário"}</button>}
                  </div>
                </div>
              );
            })
          )}

          {canWrite && (
          <div className="pt-2">
            {replyToId && <div className="flex flex-wrap items-center gap-2 text-xs"><span>Respondendo a {users.find((person) => person.id === comments.find((parent) => parent.id === replyToId)?.userId)?.name ?? "comentário"}</span><button type="button" disabled={saving} className="focus-ring min-h-11 px-2 text-[var(--accent)]" onClick={() => setReplyToId(null)}>Cancelar resposta</button></div>}
            {error && <p role="alert" className="py-2 text-xs text-[var(--red)]">{error}</p>}
            <textarea
              ref={textarea}
              disabled={saving}
              aria-label={replyToId ? "Sua resposta" : "Seu comentário"}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  void submit();
                }
              }}
              placeholder={`Leave a comment… ${"@<bot-handle>"} to trigger an agent`}
              rows={2}
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-2 text-[12.5px] outline-none focus:border-[var(--accent)] resize-none"
            />
            <div className="flex flex-wrap justify-end items-center gap-2 mt-1.5">
              {workspaceId && <HumanMentionPicker workspaceId={workspaceId} onMention={(token) => setBody((current) => `${current}${current && !current.endsWith(" ") ? " " : ""}${token} `)} />}
              <span className="text-[10.5px] text-[var(--fg-muted)]">
                <kbd className="kbd">⌘</kbd> <kbd className="kbd">Enter</kbd>
              </span>
              <button
                onClick={() => void submit()}
                disabled={!body.trim() || saving}
                className="focus-ring min-h-11 px-3 py-1 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 text-white text-[12px] font-medium transition-colors"
              >
                {saving ? "Enviando…" : replyToId ? "Enviar resposta" : "Comentar"}
              </button>
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
